// Lib
import React, { useRef } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

// Utils
import { getIsLowContrastColorWithInterface } from '../../../../../../../common/colors/colorComparisonUtil';
import useComputedBackgroundColor from '../../../../../../utils/dom/useComputedBackgroundColor';
import { resolveColorData } from '../colorButtonUtils';

// Selectors
import { selectIsMobileAppMode } from '../../../../../../platform/platformSelector';

// Components
import Button from '../../../../../../components/buttons/Button';
import DualColorIcon from '../DualColorIcon';

// Constants
import { COLOR_POPUP_MODE } from './colorPopupConstants';
import { Color } from '../../../../../../../common/colors/colorConstants';

// Styles
import './ColorButton.scss';

export type ColorButtonProps = {
    selectedType: string;
    selectedStatus: string | null;
    className?: string;
    colorPopupMode: string;
    color: string | Color | null;
    secondaryColor?: string;
    isActive: boolean;
    isContextMenu?: boolean;
    isDarkModeTheme: boolean;
    setColor: (color: string | null, secondaryColor?: string | null) => void;
    disabled?: boolean;
    preventUpdateSecondaryColor?: boolean;
};

const ColorButton = ({
    color,
    secondaryColor,
    isActive,
    setColor,
    selectedType,
    selectedStatus,
    isContextMenu,
    className,
    colorPopupMode,
    isDarkModeTheme,
    preventUpdateSecondaryColor,
}: ColorButtonProps) => {
    const isMobileAppMode = useSelector(selectIsMobileAppMode);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const { colorCss, colorName } = resolveColorData(color);
    const renderedColorCss = useComputedBackgroundColor(colorCss ? null : buttonRef, [
        colorCss,
        selectedType,
        isDarkModeTheme,
    ]);

    const onColorClick = () => {
        if (!setColor) return;

        // For scenarios like updating the color bar, ensure the secondary color is not updated
        if (preventUpdateSecondaryColor) {
            setColor(colorName);
            return;
        }

        // Update secondary to the provided secondaryColor or null, undefined will NOT be updated
        setColor(colorName, secondaryColor || null);
    };

    const displayColorCss = renderedColorCss || colorCss;
    const defaultColorIsLowContrast = isMobileAppMode ? true : !isDarkModeTheme;

    const isLowContrastColor = displayColorCss
        ? getIsLowContrastColorWithInterface(displayColorCss, isDarkModeTheme)
        : defaultColorIsLowContrast;

    const isHighContrastColor = displayColorCss
        ? getIsLowContrastColorWithInterface(displayColorCss, !isDarkModeTheme)
        : !defaultColorIsLowContrast;

    const classes = classNames('ColorButton', className, selectedType, selectedStatus, colorPopupMode, {
        active: isActive,
        'no-color': !displayColorCss,
        'low-contrast-color': isLowContrastColor,
        'high-contrast-color': isHighContrastColor,
        'remove-color': !colorCss && colorPopupMode === COLOR_POPUP_MODE.COLOR_BAR,
    });

    return (
        <Button
            ref={buttonRef}
            className={classes}
            style={{ '--ui-color-button-background': colorCss } as React.CSSProperties}
            onClickFn={onColorClick}
            onContextMenu={isContextMenu ? onColorClick : undefined}
            onMouseUp={isContextMenu ? onColorClick : undefined}
        >
            {secondaryColor && <DualColorIcon secondaryColor={secondaryColor} />}
            <div className="selection-border" />
        </Button>
    );
};

export default ColorButton;
